/**
 * Shared constants for the Change Events module.
 *
 * Everything a person sees on screen is defined here, so the wording stays the
 * same on every screen. The API sends internal words (`defacement`,
 * `sitewide_asset_change`, ...). Those words are never shown. Use the LABEL maps
 * below to turn them into plain English.
 */

/**
 * Severity colours. These match the Alerts screens, because Change Events sits
 * beside Alerts and the two must agree. The Home dashboard uses a different set
 * on purpose; do not copy this one there.
 */
/**
 * One palette for severity, shared with the Home dashboard widgets.
 *
 * These are the exact values AlertTrendWidget and TopPagesWidget use. Change
 * Events had its own set - red, orange, green - so the same alert was a
 * different colour on the home page and in this section. A colour that means
 * two things is worse than no colour.
 */
export const SEVERITY_COLORS = {
  HIGH: '#FF5A00',
  MEDIUM: '#f5c518',
  LOW: '#1e88e5',
  unscored: '#A9A9A9'
};

/** Fallback colour for a severity we do not know. */
export const SEVERITY_COLOR_DEFAULT = '#A9A9A9';

/**
 * Returns the colour for a severity value, in any letter case.
 * @param {string} severity
 * @returns {string} hex colour
 */
export function severityColor(severity) {
  const key = String(severity || '').toUpperCase();
  if (key === 'HIGH' || key === 'MEDIUM' || key === 'LOW') return SEVERITY_COLORS[key];
  return SEVERITY_COLOR_DEFAULT;
}

/** Severity values the list endpoint accepts, in the order shown in filters. */
export const SEVERITY_OPTIONS = [
  { value: 'HIGH', label: 'HIGH' },
  { value: 'MEDIUM', label: 'MEDIUM' },
  { value: 'LOW', label: 'LOW' },
  { value: 'unscored', label: 'Not scored' }
];

/** Order used to draw the stacked bars, worst at the bottom. */
export const SEVERITY_STACK_ORDER = ['HIGH', 'MEDIUM', 'LOW', 'unscored'];

/**
 * The four severities, worst first, with the wording every chart legend and
 * every severity toggle in this section must use. One list, so the screens
 * cannot drift apart.
 */
export const SEVERITY_SERIES = [
  { key: 'HIGH', label: 'HIGH' },
  { key: 'MEDIUM', label: 'MEDIUM' },
  { key: 'LOW', label: 'LOW' },
  { key: 'unscored', label: 'Not scored' }
];

/** Where the chosen severities are kept between screens. */
export const SEVERITY_FILTER_STORAGE_KEY = 'changeEventsSeverities';

/**
 * Turns any severity value into one of the four keys used on screen.
 * @param {*} value
 * @returns {string} `HIGH` `MEDIUM` `LOW` or `unscored`
 */
export function severityKey(value) {
  const key = String(value || '').toUpperCase();
  if (key === 'HIGH' || key === 'MEDIUM' || key === 'LOW') return key;
  return 'unscored';
}

/**
 * Reads the severities the person last chose. Defaults to all four on, which is
 * what a first-time visitor must see.
 * @returns {string[]}
 */
export function loadSeverityFilter() {
  const all = SEVERITY_STACK_ORDER.slice();
  try {
    const raw = window.localStorage.getItem(SEVERITY_FILTER_STORAGE_KEY);
    if (!raw) return all;
    const saved = JSON.parse(raw);
    if (!Array.isArray(saved)) return all;
    // Keep only keys we still know about, so an old value cannot break a chart.
    return all.filter((key) => saved.indexOf(key) !== -1);
  } catch (err) {
    return all;
  }
}

/**
 * Remembers the chosen severities, the same way PaginationBar remembers the
 * page size.
 * @param {string[]} keys
 */
export function saveSeverityFilter(keys) {
  try {
    window.localStorage.setItem(SEVERITY_FILTER_STORAGE_KEY, JSON.stringify(Array.isArray(keys) ? keys : []));
  } catch (err) {
    // A full or blocked storage must never stop a chart from drawing.
  }
}

/**
 * Category — what the change means to a customer. `impact_type` on the wire.
 *
 * A category that comes from a judgement says "Potential". Only a category we
 * measure directly is stated as a fact: a page that cannot be fetched is
 * unreachable, and there is nothing to interpret.
 */
export const CATEGORY_LABELS = {
  defacement: 'Potential Site-Wide Defacement',
  availability: 'Site Unreachable',
  others: 'Potential Routine Change'
};

export const CATEGORY_OPTIONS = [
  { value: 'defacement', label: CATEGORY_LABELS.defacement },
  { value: 'availability', label: CATEGORY_LABELS.availability },
  { value: 'others', label: CATEGORY_LABELS.others }
];

/**
 * Minutes as the shortest true form: 90 minutes, 4 hours, 2 days.
 *
 * Used wherever a setting is quoted on screen, so the same number never reads
 * as "360 minutes" in one place and "6 hours" in another.
 *
 * @param {number|string} minutes
 * @param {string} [fallback] shown when the value is missing
 * @returns {string}
 */
export function durationText(minutes, fallback = 'the quiet period') {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return fallback;
  const say = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  if (m % 1440 === 0) return say(m / 1440, 'day');
  if (m % 60 === 0) return say(m / 60, 'hour');
  return say(m, 'minute');
}

/** The one-line meaning of each Category, for the column heading. */
export const CATEGORY_LEGEND = {
  defacement: 'Alerts with suspicious or malicious changes.',
  availability: 'The pages in the website are not reachable.',
  others: 'The changes in this alert are similar to every day noise or changes.'
};

/**
 * The Category values one screen can show, with their meanings.
 *
 * A site-wide incident is only ever a defacement or an unreachable site. An
 * aggregated alert can also be a routine change. So the heading on each screen
 * lists only what that screen can hold.
 *
 * @param {'incidents'|'grouping'} scope
 * @returns {Array<{name: string, meaning: string}>}
 */
export function categoryHelpItems(scope) {
  const keys = scope === 'incidents'
    ? ['defacement', 'availability']
    : ['defacement', 'availability', 'others'];
  return keys.map((key) => ({ name: CATEGORY_LABELS[key], meaning: CATEGORY_LEGEND[key] }));
}

/** Every Change Type, with its meaning. */
export function changeTypeHelpItems() {
  return Object.keys(CHANGE_TYPE_LABELS).map((key) => ({
    name: CHANGE_TYPE_LABELS[key],
    meaning: CHANGE_TYPE_LEGEND[key]
  }));
}

/** Every reason an alert can be in a group, with its meaning. */
export function joinRuleHelpItems() {
  return Object.keys(JOIN_RULE_LABELS).map((key) => ({
    name: JOIN_RULE_LABELS[key],
    meaning: JOIN_RULE_LEGEND[key]
  }));
}

/**
 * Change Type — the shape of the change. `class` on the wire, in plain words.
 *
 * A type we measure is stated as a fact. A type we infer says "Potential".
 *
 * Every group holds one website only, so no type ever means "more than one
 * site". "Mixed" means more than one kind of file changed on the same site.
 */
export const CHANGE_TYPE_LABELS = {
  sitewide_asset_change: 'Site-Wide File Change',
  downtime_blast: 'Simultaneous Page Downtime',
  likely_deploy: 'Potential Content Update',
  mixed: 'Mixed File Changes',
  isolated: 'Limited Page Change'
};

/**
 * The one-line meaning of each Change Type.
 *
 * Only one type says "Potential". The other four are measured: the file names,
 * the page count and the fetch result are all facts. A content update is
 * inferred, because there were no file names to compare.
 */
export const CHANGE_TYPE_LEGEND = {
  sitewide_asset_change: 'The same files changed on enough pages to be site-wide.',
  downtime_blast: 'Several pages could not be fetched in the same window.',
  likely_deploy: 'Many pages changed together. No file names were available to compare.',
  mixed: 'More than one kind of file changed on this website.',
  isolated: 'Too few pages changed to be site-wide.'
};

export const CHANGE_TYPE_OPTIONS = [
  { value: 'sitewide_asset_change', label: CHANGE_TYPE_LABELS.sitewide_asset_change },
  { value: 'downtime_blast', label: CHANGE_TYPE_LABELS.downtime_blast },
  { value: 'likely_deploy', label: CHANGE_TYPE_LABELS.likely_deploy },
  { value: 'mixed', label: CHANGE_TYPE_LABELS.mixed },
  { value: 'isolated', label: CHANGE_TYPE_LABELS.isolated }
];

/** `status` of a change event. */
export const STATUS_LABELS = {
  open: 'Open',
  closed: 'Closed'
};

export const STATUS_OPTIONS = [
  { value: 'open', label: STATUS_LABELS.open },
  { value: 'closed', label: STATUS_LABELS.closed }
];

/** `status` of a site incident. */
export const INCIDENT_STATUS_LABELS = {
  active: 'Active',
  resolved: 'Resolved'
};

export const INCIDENT_STATUS_OPTIONS = [
  { value: 'active', label: INCIDENT_STATUS_LABELS.active },
  { value: 'resolved', label: INCIDENT_STATUS_LABELS.resolved }
];

/**
 * Window State — what happened to the window, not what the website is doing.
 *
 * A window is open while alerts of the same kind keep arriving. It closes
 * itself after a quiet period, or a person closes it by hand.
 */
export const RESOLVE_REASON_LABELS = {
  manual: 'Manually Resolved',
  quiet_timeout: 'Automatically Closed'
};

/** Window State for a window that is still taking alerts. */
export const WINDOW_OPEN_LABEL = 'Window Open';

/**
 * The Window State of one site incident.
 * @param {object} row an incident, as the API sent it
 * @returns {string}
 */
export function windowState(row) {
  if (!row) return '-';
  if (row.status === 'active') return WINDOW_OPEN_LABEL;
  return labelFor(RESOLVE_REASON_LABELS, row.resolve_reason, 'Window Closed');
}

/**
 * Why grouped. The internal rule names never appear on screen.
 */
export const JOIN_RULE_LABELS = {
  seed: 'Seed Alert',
  wjaccard: 'Same Files Changed',
  tprefix: 'Same Folders Changed',
  carrier: 'Still Carries Those Files',
  churn: 'Potential Routine Change',
  profile: 'Same Kinds of File',
  downtime: 'Page Unreachable'
};

/**
 * The one-line meaning of each "Why grouped" name. The legend under a table
 * shows only the reasons actually on screen, so it stays short.
 */
export const JOIN_RULE_LEGEND = {
  seed: 'This alert seeded the group. Every other alert is compared against the seed.',
  wjaccard: 'The same files changed on this page and on the seed page.',
  tprefix: 'Different files changed, but in the same folders as the seed.',
  carrier: 'This page still carries the files that seeded the group.',
  churn: 'Nothing new changed. These are files this website changes every day.',
  profile: 'No file names to compare, but the same kinds of file changed.',
  downtime: 'The page could not be fetched at the same time as the others.'
};

/**
 * What a customer receives. One setting, three values.
 *
 * Only `throttled` ever holds an individual alert back. `log_only` and `all`
 * differ in one way: whether the summaries are sent.
 *
 * NOT two switches. Two switches allow "hold the alerts and send no summary",
 * which is silence, and nothing should be able to ask for that.
 */
export const NOTIFY_MODES = [
  {
    value: 'log_only',
    label: 'Record Only',
    detail: 'Alerts are sent as they are received. No summaries are sent, but they are recorded and shown in the portal.'
  },
  {
    value: 'all',
    label: 'All Alerts and Summaries',
    detail: 'Alerts are sent as they are received. Summaries are also sent, and shown in the portal.'
  },
  {
    value: 'throttled',
    label: 'Summaries Only',
    // The only option that stops an email reaching a customer. The screen puts
    // a tag beside it saying so, where the choice is made.
    holdsAlertsBack: true,
    detail:
      'While a window is open, individual alerts are held back. One summary is sent for the window, and shown in the portal.'
  }
];

/** The mode that changes nothing, used when the API sends none. */
export const NOTIFY_MODE_DEFAULT = 'log_only';

/** Sort values the list endpoint accepts. */
export const SORT_OPTIONS = [
  { value: 'alerts', label: 'Most alerts' },
  { value: 'pages', label: 'Most pages' },
  { value: 'last_seen', label: 'Last seen' },
  { value: 'first_seen', label: 'First seen' }
];

export const DEFAULT_SORT = 'alerts';

/**
 * Looks a value up in a label map, and falls back to a readable form of the raw
 * value if the API sends something new.
 * @param {Record<string,string>} map
 * @param {string} value
 * @param {string} [fallback] shown when the value is empty
 * @returns {string}
 */
export function labelFor(map, value, fallback = '-') {
  if (value === null || value === undefined || value === '') return fallback;
  const key = String(value);
  if (map[key]) return map[key];
  return key.replace(/_/g, ' ');
}

/**
 * The Category of a change event or site incident.
 *
 * The API sends `category` as a ready display string. Older replies only carry
 * the raw `impact_type` code, so that is mapped here as a fallback. A display
 * string that is really a raw code is mapped too, so a half-updated API cannot
 * put `others` on screen.
 * @param {object} row a change event or a site incident
 * @returns {string}
 */
export function categoryLabel(row) {
  if (!row) return '-';
  // The value decides the words, and the words are the ones the column help
  // uses. The API also sends a worded `category`; it is only a fallback for a
  // row with no value, so the table and its help can never disagree.
  if (row.impact_type && CATEGORY_LABELS[row.impact_type]) return CATEGORY_LABELS[row.impact_type];
  const ready = row.category;
  if (ready && CATEGORY_LABELS[ready]) return CATEGORY_LABELS[ready];
  if (ready && String(ready).trim()) return String(ready).trim();
  return labelFor(CATEGORY_LABELS, row.impact_type);
}

/**
 * The Change Type of a change event, in plain words.
 *
 * Prefers the API's ready `change_type` string, and falls back to mapping the
 * raw `class` code.
 * @param {object} row a change event
 * @returns {string}
 */
export function changeTypeLabel(row) {
  if (!row) return '-';
  // Same rule as categoryLabel: the value decides, the help's words are used.
  if (row.class && CHANGE_TYPE_LABELS[row.class]) return CHANGE_TYPE_LABELS[row.class];
  const ready = row.change_type;
  if (ready && CHANGE_TYPE_LABELS[ready]) return CHANGE_TYPE_LABELS[ready];
  if (ready && String(ready).trim()) return String(ready).trim();
  return labelFor(CHANGE_TYPE_LABELS, row.class);
}

/**
 * The severity mix of a change event, ready to draw.
 *
 * Returns `null` when the API sent no `severity_mix`. That means the row has
 * not been recounted yet, and four zeroes would be a lie — the screen shows the
 * worst severity alone instead.
 * @param {object} row a change event carrying `severity_mix`
 * @returns {Array<{key: string, label: string, count: number, color: string}>|null}
 */
export function severityMix(row) {
  // A site-wide incident sends severity_counts, which holds only HIGH and
  // MEDIUM because those are the only severities that can open one. An
  // aggregated alert sends severity_mix, which holds all four. Both are read
  // the same way; whichever is present wins.
  const raw = row && (row.severity_mix || row.severity_counts);
  if (!raw || typeof raw !== 'object') return null;
  const parts = [];
  SEVERITY_STACK_ORDER.forEach((key) => {
    // The API sends the keys in lower case (`high`, `medium`, ...); `unscored`
    // is already lower case in both places.
    const value = raw[key.toLowerCase()] != null ? raw[key.toLowerCase()] : raw[key];
    const count = Number(value);
    if (!Number.isFinite(count) || count <= 0) return;
    const series = SEVERITY_SERIES.find((s) => s.key === key);
    parts.push({
      key,
      label: series ? series.label : key,
      count,
      color: severityColor(key)
    });
  });
  return parts;
}

/**
 * The severity mix as one line of text, for a tooltip or a screen reader:
 * `HIGH 1 · MEDIUM 12 · LOW 387`.
 * @param {Array<{label: string, count: number}>|null} parts
 * @returns {string}
 */
export function severityMixText(parts) {
  if (!Array.isArray(parts) || !parts.length) return '';
  return parts.map((part) => `${part.label} ${part.count.toLocaleString('en-US')}`).join(' · ');
}

/**
 * The name of a webpage, for a person to read.
 *
 * A customer must never be shown a bare `url_id`. The API sends `page_name` and
 * `page_url` on every alert, so use the title, then the address, and only say
 * "Unnamed page" when neither arrived.
 * @param {object} row anything carrying `page_name` / `page_url`
 * @returns {string}
 */
export function pageName(row) {
  if (!row) return 'Unnamed page';
  const name = row.page_name || row.pageName;
  if (name && String(name).trim()) return String(name).trim();
  const url = pageUrl(row);
  if (url) return url;
  return 'Unnamed page';
}

/**
 * The address of a webpage, or an empty string when the API did not send one.
 * @param {object} row
 * @returns {string}
 */
export function pageUrl(row) {
  if (!row) return '';
  const url = row.page_url || row.pageUrl || row.webpage_url || (row.url && row.url.url);
  return url ? String(url).trim() : '';
}

/**
 * Reads a list of file names that the API may send in several shapes.
 *
 * A long list is cut short by the API, which then says how many more there are.
 * Accepts a plain array, or `{ files: [...], more: 12 }`, or the same idea
 * spelled `items` / `total`, so a change of spelling cannot empty the panel.
 * @param {*} raw
 * @returns {{files: string[], more: number}}
 */
export function fileList(raw) {
  const empty = { files: [], more: 0 };
  if (!raw) return empty;
  if (Array.isArray(raw)) return { files: raw.filter(Boolean).map(String), more: 0 };
  if (typeof raw === 'string') {
    return { files: raw.split(',').map((f) => f.trim()).filter(Boolean), more: 0 };
  }
  if (typeof raw !== 'object') return empty;
  const files = fileList(raw.files || raw.items || raw.list || raw.names).files;
  const more = Number(raw.more != null ? raw.more : raw.more_count);
  if (Number.isFinite(more) && more > 0) return { files, more };
  // Some replies send the full count instead of the leftover.
  const total = Number(raw.total != null ? raw.total : raw.count);
  if (Number.isFinite(total) && total > files.length) return { files, more: total - files.length };
  return { files, more: 0 };
}

/** The limits the settings form enforces. They match the API contract. */
export const CONFIG_LIMITS = {
  flood_threshold: { min: 1, max: 1000, default: 5 },
  flood_window_minutes: { min: 5, max: 1440, default: 60 },
  group_window_minutes: { min: 15, max: 1440, default: 240 },
  auto_resolve_minutes: { min: 30, max: 10080, default: 360 },
  similarity_threshold: { min: 0.05, max: 0.95, step: 0.05, default: 0.3 },
  backoff_minutes: { minCount: 1, maxCount: 10, min: 5, max: 10080, default: [60, 240, 720, 1440] }
};

/**
 * The furthest back any Change Events screen may ask for. The quick ranges in
 * `dateRanges.js` and the pickers' `disabledDate` both read this.
 */
export const MAX_MONTHS_BACK = 3;

/**
 * The rule that opens a site-wide incident, in one sentence, so the settings
 * help and the banner above the incident list cannot disagree.
 *
 * The service opens a window when the count of HIGH or MEDIUM alerts of one
 * category on one website goes ABOVE the threshold (`countInWindow <=
 * threshold` is still sent individually), so the word is "more than".
 * @param {number|string} threshold the number, or a phrase such as "this many"
 *   for the settings help
 * @returns {string}
 */
export function floodRuleText(threshold) {
  const n = Number(threshold);
  let count = '-';
  if (Number.isFinite(n) && threshold !== '' && threshold != null) count = n.toLocaleString('en-US');
  else if (typeof threshold === 'string' && threshold.trim()) count = threshold.trim();
  return `More than ${count} HIGH or MEDIUM alerts of the same category on one website`;
}

/**
 * A count for a person to read: `1,234`. Anything that is not a number reads
 * as `0`, so a missing field never shows `NaN`.
 * @param {*} value
 * @returns {string}
 */
export function formatNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('en-US');
}

/**
 * The severity as a label: `HIGH`, `MEDIUM`, `LOW`, or `NOT SCORED`.
 * @param {*} value
 * @returns {string}
 */
export function severityLabel(value) {
  const key = severityKey(value);
  return key === 'unscored' ? 'NOT SCORED' : key;
}

/**
 * "3.2x fewer rows" for a grouping ratio, or an empty string when grouping
 * made no difference or the ratio is not a number.
 * @param {*} ratio
 * @returns {string}
 */
export function ratioLabel(ratio) {
  const num = Number(ratio);
  if (!Number.isFinite(num) || num <= 1) return '';
  return `${num.toFixed(1)}x fewer rows`;
}
